/**
 * North Indian Style Vedic Diamond Chart (SVG)
 * - Large rashi numbers (1-12) visible in each house
 * - Big planet abbreviations with retrograde indicator
 * - Lagna marker prominent in House 1
 */
import React from 'react';

const PLANET_HI_SHORT = {
  'सूर्य': 'सू', 'चंद्र': 'चं', 'मंगल': 'मं', 'बुध': 'बु', 'गुरु': 'गु',
  'शुक्र': 'शु', 'शनि': 'श', 'राहु': 'रा', 'केतु': 'के',
};
const PLANET_EN_SHORT = {
  Sun: 'Su', Moon: 'Mo', Mars: 'Ma', Mercury: 'Me', Jupiter: 'Ju',
  Venus: 'Ve', Saturn: 'Sa', Rahu: 'Ra', Ketu: 'Ke',
};

// North Indian diamond house layout (standard, anti-clockwise from top)
// Standard layout: 4 large central diamonds (houses 1=top, 4=left, 7=bottom, 10=right)
// + 8 small corner triangles (houses 2,3,5,6,8,9,11,12). All non-overlapping.
const HOUSES = [
  { id: 1, points: '200,0 100,100 200,200 300,100', cx: 200, cy: 100, rx: 200, ry: 95 },
  { id: 2, points: '0,0 200,0 100,100', cx: 100, cy: 50, rx: 100, ry: 30 },
  { id: 3, points: '0,0 100,100 0,200', cx: 50, cy: 100, rx: 35, ry: 105 },
  { id: 4, points: '0,200 100,100 200,200 100,300', cx: 100, cy: 200, rx: 100, ry: 195 },
  { id: 5, points: '0,200 100,300 0,400', cx: 50, cy: 300, rx: 35, ry: 295 },
  { id: 6, points: '0,400 100,300 200,400', cx: 100, cy: 350, rx: 100, ry: 380 },
  { id: 7, points: '200,400 100,300 200,200 300,300', cx: 200, cy: 300, rx: 200, ry: 295 },
  { id: 8, points: '200,400 300,300 400,400', cx: 300, cy: 350, rx: 300, ry: 380 },
  { id: 9, points: '400,200 300,300 400,400', cx: 350, cy: 300, rx: 365, ry: 295 },
  { id: 10, points: '400,200 300,300 200,200 300,100', cx: 300, cy: 200, rx: 300, ry: 195 },
  { id: 11, points: '400,0 400,200 300,100', cx: 350, cy: 100, rx: 365, ry: 105 },
  { id: 12, points: '200,0 400,0 300,100', cx: 300, cy: 50, rx: 300, ry: 30 },
];

export default function VedicChart({ lagnaSignIdx = 0, planets = [], testId = 'vedic-chart' }) {
  const houseSign = {};
  for (let h = 1; h <= 12; h++) {
    houseSign[h] = ((lagnaSignIdx + h - 1) % 12) + 1; // Rashi number 1-12
  }

  const planetsByHouse = {};
  for (let h = 1; h <= 12; h++) planetsByHouse[h] = [];
  planets.forEach(p => {
    if (p.house) planetsByHouse[p.house].push(p);
  });

  return (
    <div className="vc-wrap" data-testid={testId}>
      <svg viewBox="0 0 400 400" className="vc-svg" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid meet">
        <rect x="0" y="0" width="400" height="400" fill="#1a1333" stroke="#FFD700" strokeWidth="2.5"/>
        <line x1="0" y1="0" x2="400" y2="400" stroke="#FFD700" strokeWidth="2" opacity="0.85"/>
        <line x1="400" y1="0" x2="0" y2="400" stroke="#FFD700" strokeWidth="2" opacity="0.85"/>
        <polygon points="200,0 400,200 200,400 0,200" fill="none" stroke="#FFD700" strokeWidth="2" opacity="0.95"/>

        {HOUSES.map(h => {
          const rashiNum = houseSign[h.id];
          const planetsHere = planetsByHouse[h.id] || [];
          const isLagna = h.id === 1;

          // Rashi number position - corner of house (small, gold)
          let numX = h.cx;
          let numY = h.cy;
          if (h.id === 1) { numX = 200; numY = 50; }
          else if (h.id === 4) { numX = 52; numY = 200; }
          else if (h.id === 7) { numX = 200; numY = 252; }
          else if (h.id === 10) { numX = 348; numY = 200; }
          else if (h.id === 2) { numX = 130; numY = 35; }
          else if (h.id === 3) { numX = 35; numY = 75; }
          else if (h.id === 5) { numX = 35; numY = 325; }
          else if (h.id === 6) { numX = 130; numY = 365; }
          else if (h.id === 8) { numX = 270; numY = 365; }
          else if (h.id === 9) { numX = 365; numY = 325; }
          else if (h.id === 11) { numX = 365; numY = 75; }
          else if (h.id === 12) { numX = 270; numY = 35; }

          return (
            <g key={h.id}>
              {/* BIG rashi number inside house (prominent) */}
              <text
                x={numX}
                y={numY}
                textAnchor="middle"
                fill="#FFD700"
                fontSize="20"
                fontWeight="700"
                fontFamily="'Cormorant Garamond', serif"
                opacity="0.9"
              >
                {rashiNum}
              </text>

              {/* Lagna marker in house 1 */}
              {isLagna && (
                <text
                  x="200"
                  y="172"
                  textAnchor="middle"
                  fill="#FF6B35"
                  fontSize="11"
                  fontWeight="700"
                  fontFamily="'Cormorant Garamond', serif"
                >
                  ◆ लग्न
                </text>
              )}

              {/* Planet abbreviations (large, bold) */}
              {planetsHere.map((p, idx) => {
                const n = planetsHere.length;
                // Arrange planets in a grid: max 3 per row
                const cols = n <= 2 ? n : n <= 4 ? 2 : 3;
                const col = idx % cols;
                const row = Math.floor(idx / cols);
                const rows = Math.ceil(n / cols);
                const spacingX = 32;
                const spacingY = 20;
                const offsetX = (col - (cols - 1) / 2) * spacingX;
                const offsetY = (row - (rows - 1) / 2) * spacingY + (isLagna ? 24 : 18);

                const short = PLANET_HI_SHORT[p.nameHi] || PLANET_EN_SHORT[p.name] || p.name?.slice(0, 2);
                const color = p.retrograde ? '#FF6B35' : '#FFF5CC';

                return (
                  <g key={idx}>
                    <text
                      x={h.cx + offsetX}
                      y={h.cy + offsetY}
                      textAnchor="middle"
                      fill={color}
                      fontSize="17"
                      fontWeight="700"
                      fontFamily="'Cormorant Garamond', serif"
                      stroke="#1a1333"
                      strokeWidth="0.3"
                    >
                      {short}{p.retrograde ? '↩' : ''}
                    </text>
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
    </div>
  );
}
